import { interpolateGreatCircle, type LatLon } from '@trips/shared/geo';
import { flownShare, type PlaneFix } from '@trips/shared/plane-position';
import type { TripLeg } from '@trips/shared/trip-model';
import { useMemo } from 'react';
import { Icon } from '../../ui/Icon';
import { MiniMap } from '../../ui/MiniMap';
import s from './FlightMap.module.css';
import { PLANE_PATH } from './planeShape';

const STEPS = 64;

/** A point a little way along the plane's heading, to aim the drawing on the flat map. */
function ahead(p: PlaneFix): LatLon {
  const r = (p.heading * Math.PI) / 180;
  const shrink = Math.max(0.2, Math.cos((p.lat * Math.PI) / 180));
  return { lat: p.lat + Math.cos(r) * 0.5, lon: p.lon + (Math.sin(r) * 0.5) / shrink };
}

/**
 * The flight on a map: the great-circle route over the bundled world outline (no tiles, so it
 * draws in the air without wifi), the part already flown solid, and the plane where it is (its
 * reported position, or the estimate).
 */
export function FlightMap({
  from,
  to,
  codes,
  progress,
  plane,
  onOpen,
}: {
  from: LatLon;
  to: LatLon;
  codes: [string, string];
  /** Share flown, for the solid line when the plane isn't drawn (after landing). */
  progress: number;
  /** Where my plane is while it's in the air. */
  plane: PlaneFix | null;
  onOpen?: () => void;
}) {
  const legs = useMemo<TripLeg[]>(
    () => [
      {
        itemId: 'flight',
        kind: 'flight',
        from: { ...from, code: codes[0], name: codes[0] },
        to: { ...to, code: codes[1], name: codes[1] },
      },
    ],
    [from, to, codes],
  );
  const route = useMemo(
    () => Array.from({ length: STEPS + 1 }, (_, i) => interpolateGreatCircle(from, to, i / STEPS)),
    [from, to],
  );

  return (
    <button type="button" className={s.box} onClick={onOpen} aria-label="Open the flight map">
      <MiniMap
        legs={legs}
        width={360}
        height={190}
        variant="cover"
        bare
        overlay={(proj) => {
          const pts = route.map(proj);
          const line = (list: typeof pts) =>
            list.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('');
          const share = plane ? flownShare(from, to, plane) : Math.min(1, Math.max(0, progress));
          const cut = Math.round(share * STEPS);
          const [a, b] = [pts[0]!, pts[STEPS]!];
          let drawn = null;
          if (plane) {
            const here = proj(plane);
            const next = proj(ahead(plane));
            const angle = (Math.atan2(next[1] - here[1], next[0] - here[0]) * 180) / Math.PI;
            drawn = (
              <g transform={`translate(${here[0]} ${here[1]}) rotate(${angle}) scale(1.35)`}>
                <path d={PLANE_PATH} className={s.plane} />
              </g>
            );
          }
          return (
            <g>
              <path d={line(pts)} className={s.ahead} />
              {cut > 0 && <path d={line(pts.slice(0, cut + 1))} className={s.flown} />}
              <circle cx={a[0]} cy={a[1]} r={4.5} className={s.end} />
              <circle cx={b[0]} cy={b[1]} r={4.5} className={s.end} />
              <text x={a[0]} y={a[1] - 10} className={s.code} textAnchor="middle">
                {codes[0]}
              </text>
              <text x={b[0]} y={b[1] - 10} className={s.code} textAnchor="middle">
                {codes[1]}
              </text>
              {drawn}
            </g>
          );
        }}
      />
      <span className={s.expand} aria-hidden="true">
        <Icon name="arrows-out-simple" size={16} />
      </span>
    </button>
  );
}
